import { ThunkDispatch } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchEvent,
  fromEvents,
  updateEvent,
  addUsers,
  fetchEventUsers,
  removeUser,
  saveArtifactsOrder,
  addArtifact,
  updateArtifact,
  submitForReview,
  saveMatchingCategories,
  eventsSlice,
} from './events';
import { Event, ParentTagName } from './eventsTypes';
import { useDebouncedCallback } from 'utils/debounce-promise';
import { Artifact } from './artifactsTypes';

/**
 * A custom hook that provides the saving state from the events store.
 *
 * @returns {boolean} A boolean indicating if any save operation is currently in progress
 *
 * @example
 * const saving = useSaving();
 * if (saving) {
 *   // Show loading indicator
 * }
 */
export const useSaving = () => {
  return useSelector((state: Map<string, any>) => fromEvents.getIsSaving(state?.toJS()));
};

/**
 * A custom hook that fetches and manages an event by its ID.
 * The hook automatically fetches the event on mount and provides a way to refetch it.
 *
 * @param {string} eventId - The unique identifier of the event to fetch
 * @returns {Object} An object containing:
 *   - event: The event data from the Redux store
 *   - refetchEvent: A function to manually trigger a refresh of the event data
 *
 * @example
 * const { event, refetchEvent } = useFetchEvent('123');
 * console.log(event);
 * refetchEvent();
 */
export const useFetchEvent = (eventId: string) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const event = useSelector((state: Map<string, any>) =>
    fromEvents.selectById(state?.toJS(), eventId),
  );

  const refetchEvent = useCallback(() => {
    dispatch(fetchEvent(eventId));
  }, [dispatch, eventId]);

  useEffect(() => {
    refetchEvent();
  }, [refetchEvent]);

  return { refetchEvent, event };
};

/**
 * A custom React hook for updating (saving) an event and tracking the saving state.
 *
 * This hook provides a function to update an event by dispatching an update action,
 * and a boolean flag indicating whether an update operation is currently in progress.
 *
 * @returns {{ saving: boolean, update: (event: Event) => void }} An object containing:
 *   - saving: A boolean indicating if an event update is currently in progress.
 *   - update: A function that takes an Event object and dispatches an update action to the store.
 *
 * @example
 * const { update, saving } = useUpdateEvent();
 * update(event); // Dispatches the update action for the event
 * if (saving) {
 *    Show a loading indicator while the event is being saved
 * }
 */
export const useUpdateEvent = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSaving();

  const update = useCallback((event: Event) => dispatch(updateEvent(event)), [dispatch]);

  return { saving, update };
};

/**
 * A custom React hook for adding users to an event and tracking the saving state.
 *
 * This hook provides a function to add users to an event by dispatching an addUsers action,
 * and a boolean flag indicating whether an add users operation is currently in progress.
 *
 * @returns {{ saving: boolean, addUsersToEvent: (eventId: string, userType: string, users: string[]) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if an add users operation is currently in progress.
 *   - addUsersToEvent: A function that takes eventId, userType, and users array and dispatches an addUsers action to the store.
 *
 * @example
 * const { addUsersToEvent, saving } = useAddUsers();
 * await addUsersToEvent('event123', 'organizer', ['email1@test.com', 'email2@test.com']);
 * if (saving) {
 *    Show a loading indicator while users are being added
 * }
 */
export const useAddUsers = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSaving();

  const addUsersFn = useCallback(
    (eventId: string, userType: string, emails: string[]) =>
      dispatch(addUsers({ eventId, userType, emails })),
    [dispatch],
  );

  return { saving, addUsers: addUsersFn };
};

/**
 * A custom React hook for fetching event users (organizers and navigators) and tracking the loading state.
 *
 * This hook provides a function to fetch event users by dispatching a fetchEventUsers action,
 * and returns the organizers, navigators, and loading state.
 *
 * @param {string} eventId - The unique identifier of the event
 * @returns {{ navigators: EventUser[], organizers: EventUser[], isLoadingUsers: boolean, fetchUsers: () => void }} An object containing:
 *   - navigators: Array of navigator users for the event
 *   - organizers: Array of organizer users for the event
 *   - isLoadingUsers: A boolean indicating if a fetch users operation is currently in progress
 *   - fetchUsers: A function to manually trigger fetching of event users
 *
 * @example
 * const { navigators, organizers, isLoadingUsers, fetchUsers } = useFetchEventUsers('event123');
 * fetchUsers(); // Manually trigger fetch
 * if (isLoadingUsers) {
 *    Show a loading indicator while users are being fetched
 * }
 */
export const useFetchEventUsers = (eventId: string) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigators = useSelector((state: Map<string, any>) =>
    fromEvents.getNavigatorsByEventId(state?.toJS(), eventId),
  );
  const organizers = useSelector((state: Map<string, any>) =>
    fromEvents.getOrganizersByEventId(state?.toJS(), eventId),
  );
  const isLoadingUsers = useSelector((state: Map<string, any>) =>
    fromEvents.getIsLoadingUsers(state?.toJS()),
  );

  const fetchUsers = useCallback(() => {
    dispatch(fetchEventUsers(eventId));
  }, [dispatch, eventId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers, eventId]);

  return { navigators, organizers, isLoadingUsers, fetchUsers };
};

/**
 * A custom React hook for removing a user from an event and tracking the loading state.
 *
 * This hook provides a function to remove a user from an event by dispatching a removeUser action,
 * and a boolean flag indicating whether a remove user operation is currently in progress.
 *
 * @returns {{ isLoadingUsers: boolean, removeUserFromEvent: (eventId: string, userId: string) => Promise<any> }} An object containing:
 *   - isLoadingUsers: A boolean indicating if a remove user operation is currently in progress.
 *   - removeUserFromEvent: A function that takes eventId and userId and dispatches a removeUser action to the store.
 *
 * @example
 * const { removeUserFromEvent, isLoadingUsers } = useRemoveUser();
 * await removeUserFromEvent('event123', 'user456');
 * if (isLoadingUsers) {
 *    Show a loading indicator while user is being removed
 * }
 */
export const useRemoveUser = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const isLoadingUsers = useSelector((state: Map<string, any>) =>
    fromEvents.getIsLoadingUsers(state?.toJS()),
  );

  const removeUserFromEvent = useCallback(
    (eventId: string, userId: string) => dispatch(removeUser({ eventId, userId })),
    [dispatch],
  );

  return { isLoadingUsers, removeUserFromEvent };
};

/**
 * A custom React hook for retrieving inherited matching categories for an event filtered by parent category.
 *
 * This hook provides access to the inherited matching categories for a specific event,
 * which are categories that are both enabled and inherited for the event, filtered by parent category.
 *
 * @param {Object} params - The parameters object
 * @param {string} params.eventId - The unique identifier of the event
 * @param {ParentTagName} params.parentCategoryName - The parent category name to filter by
 * @returns {MatchingCategory[]} An array of inherited matching categories for the event filtered by parent category
 *
 * @example
 * const inheritedCategories = useInheritedMatchingCategories({ eventId: 'event123', parentCategoryName: ParentTagName.STAGE_OF_VENTURE });
 * console.log(inheritedCategories); // Array of inherited categories for Stage of Venture
 */
export const useInheritedMatchingCategories = (params: {
  eventId: string;
  parentCategoryName: ParentTagName;
}) => {
  return useSelector((state: Map<string, any>) =>
    fromEvents.getInheritedMatchingCategoriesByEventId(state?.toJS(), params),
  );
};

/**
 * A custom React hook for retrieving matching categories for an event filtered by parent category.
 *
 * This hook provides access to the matching categories for a specific event,
 * which are categories that are enabled but not inherited, filtered by parent category.
 *
 * @param {Object} params - The parameters object
 * @param {string} params.eventId - The unique identifier of the event
 * @param {ParentTagName} params.parentCategoryName - The parent category name to filter by
 * @returns {MatchingCategory[]} An array of matching categories for the event filtered by parent category
 *
 * @example
 * const matchingCategories = useMatchingCategories({ eventId: 'event123', parentCategoryName: ParentTagName.STAGE_OF_VENTURE });
 * console.log(matchingCategories); // Array of matching categories for Stage of Venture
 */
export const useMatchingCategories = (params: {
  eventId: string;
  parentCategoryName: ParentTagName;
}) => {
  return useSelector((state: Map<string, any>) =>
    fromEvents.getMatchingCategoriesByEventId(state?.toJS(), params),
  );
};

/**
 * A custom React hook for retrieving all matching categories for an event filtered by parent category.
 *
 * This hook provides access to all matching categories for a specific event,
 * filtered by parent category, without any inheritance exclusions.
 *
 * @param {Object} params - The parameters object
 * @param {string} params.eventId - The unique identifier of the event
 * @param {ParentTagName} params.parentCategoryName - The parent category name to filter by
 * @returns {MatchingCategory[]} An array of all matching categories for the event filtered by parent category
 *
 * @example
 * const allMatchingCategories = useAllMatchingCategories({ eventId: 'event123', parentCategoryName: ParentTagName.STAGE_OF_VENTURE });
 * console.log(allMatchingCategories); // Array of all matching categories for Stage of Venture
 */
export const useAllMatchingCategories = (params: {
  eventId: string;
  parentCategoryName: ParentTagName;
}) => {
  return useSelector((state: Map<string, any>) =>
    fromEvents.getAllMatchingCategoriesByEventId(state?.toJS(), params),
  );
};

/**
 * A custom React hook for saving the order of artifacts in an event and tracking the saving state.
 *
 * This hook provides a function to save artifacts order by dispatching a saveArtifactsOrder action,
 * and a boolean flag indicating whether a save operation is currently in progress.
 *
 * @returns {{ saving: boolean, saveOrder: (eventId: string, artifactIds: number[]) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if a save artifacts order operation is currently in progress.
 *   - saveOrder: A function that takes eventId and artifactIds array and dispatches a saveArtifactsOrder action to the store.
 *
 * @example
 * const { saveOrder, saving } = useSaveArtifactsOrder();
 * await saveOrder('event123', ['artifact1', 'artifact2', 'artifact3']);
 * if (saving) {
 *    Show a loading indicator while the order is being saved
 * }
 */
export const useSaveArtifactsOrder = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSaving();

  const saveOrder = useCallback(
    (eventId: string, artifactIds: number[]) =>
      dispatch(saveArtifactsOrder({ eventId, artifactIds })),
    [dispatch],
  );

  return { saving, saveOrder };
};

/**
 * A custom React hook for adding an artifact to an event and tracking the saving state.
 *
 * This hook provides a function to add an artifact by dispatching an addArtifact action,
 * and a boolean flag indicating whether a save operation is currently in progress.
 *
 * @returns {{ saving: boolean, addArtifactToEvent: (eventId: string, artifact: Artifact) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if an add artifact operation is currently in progress.
 *   - addArtifactToEvent: A function that takes eventId and artifact data and dispatches an addArtifact action to the store.
 *
 * @example
 * const { addArtifactToEvent, saving } = useAddArtifact();
 * await addArtifactToEvent('event123', { teams_and_organizers_name: 'Doc', type: 1, ... });
 * if (saving) {
 *    Show a loading indicator while the artifact is being saved
 * }
 */
export const useAddArtifact = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSaving();

  const addArtifactToEvent = useCallback(
    (eventId: string, artifact: Artifact) => dispatch(addArtifact({ eventId, artifact })),
    [dispatch],
  );

  return { saving, addArtifactToEvent };
};

/**
 * A custom React hook for updating an artifact in an event and tracking the saving state.
 *
 * This hook provides a function to update an artifact by dispatching an updateArtifact action,
 * and a boolean flag indicating whether a save operation is currently in progress.
 *
 * @returns {{ saving: boolean, updateArtifactInEvent: (eventId: string, artifactId: number, artifact: Artifact) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if an update artifact operation is currently in progress.
 *   - updateArtifactInEvent: A function that takes eventId, artifactId and artifact data and dispatches an updateArtifact action to the store.
 *
 * @example
 * const { updateArtifactInEvent, saving } = useUpdateArtifact();
 * await updateArtifactInEvent('event123', 456, { teams_and_organizers_name: 'Updated Doc', type: 1, ... });
 * if (saving) {
 *    Show a loading indicator while the artifact is being updated
 * }
 */
export const useUpdateArtifact = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSaving();

  const updateArtifactInEvent = useCallback(
    (eventId: string, artifactId: number, artifact: Artifact) =>
      dispatch(updateArtifact({ eventId, artifactId, artifact })),
    [dispatch],
  );

  return { saving, updateArtifactInEvent };
};

/**
 * A custom React hook for reordering artifacts in an event.
 *
 * This hook provides a function to reorder artifacts by moving an artifact from one position
 * to another in the artifacts array of an event.
 *
 * @returns {{ reorderArtifacts: (eventId: string, fromIndex: number, toIndex: number) => void }} An object containing:
 *   - reorderArtifacts: A function that takes eventId, fromIndex, and toIndex and reorders the artifacts array.
 *
 * @example
 * const { reorderArtifacts } = useReorderArtifacts();
 * reorderArtifacts('event123', 2, 0); // Moves artifact from position 2 to position 0
 */
export const useReorderArtifacts = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { saveOrder } = useSaveArtifactsOrder();
  const saving = useSaving();

  const debouncedSaveOrder = useDebouncedCallback(
    async (...args: any[]) => {
      const [eventId, artifacts] = args as [string, Artifact[]];
      saveOrder(
        eventId,
        artifacts.map((artifact) => artifact.id),
      );
    },
    [],
    1000,
  );

  const reorderArtifacts = useCallback(
    (eventId: string, fromIndex: number, toIndex: number) => {
      const result = dispatch(
        eventsSlice.actions.reorderArtifacts({ eventId, fromIndex, toIndex }),
      );
      return debouncedSaveOrder(eventId, result.payload.artifacts);
    },
    [dispatch],
  );

  return { reorderArtifacts, saving };
};

/**
 * A custom React hook for submitting an event for review and tracking the saving state.
 *
 * This hook provides a function to submit an event for review by dispatching a submitForReview action,
 * and a boolean flag indicating whether a submit operation is currently in progress.
 *
 * @returns {{ saving: boolean, submitEventForReview: (eventId: string, organizerId: string, subject: string, content: string) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if a submit for review operation is currently in progress.
 *   - submitEventForReview: A function that takes eventId, organizerId, subject, and content and dispatches a submitForReview action to the store.
 *
 * @example
 * const { submitEventForReview, saving } = useSubmitForReview();
 * await submitEventForReview('event123', 'organizer456', 'Review Subject', 'Please review this event...');
 * if (saving) {
 *    Show a loading indicator while the submission is being processed
 * }
 */
export const useSubmitForReview = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSaving();

  const submitEventForReview = useCallback(
    (eventId: string, organizerId: string, subject: string, content: string) =>
      dispatch(submitForReview({ eventId, organizerId, subject, content })),
    [dispatch],
  );

  return { saving, submitEventForReview };
};

/**
 * A custom React hook for saving matching categories for an event and tracking the saving state.
 *
 * This hook provides a function to save matching categories by dispatching a saveMatchingCategories action,
 * and a boolean flag indicating whether a save operation is currently in progress.
 *
 * @returns {{ saving: boolean, saveMatchingCategories: (eventId: string, tagIds: string[]) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if a save matching categories operation is currently in progress.
 *   - saveMatchingCategories: A function that takes eventId and tagIds array and dispatches a saveMatchingCategories action to the store.
 *
 * @example
 * const { saveMatchingCategories, saving } = useSaveMatchingCategories();
 * await saveMatchingCategories('event123', ['tag1', 'tag2', 'tag3']);
 * if (saving) {
 *    Show a loading indicator while the categories are being saved
 * }
 */
export const useSaveMatchingCategories = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSaving();

  const saveMatchingCategoriesFn = useCallback(
    (eventId: string, tagIds: string[]) => dispatch(saveMatchingCategories({ eventId, tagIds })),
    [dispatch],
  );

  return { saving, saveMatchingCategories: saveMatchingCategoriesFn };
};
